import { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, KeyRound, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { useAdminAuth } from "../../context/AdminAuthContext";

const EMPTY_FORM = { email: "", password: "", name: "" };

export default function AdminsPage() {
  const { adminAxios, admin: currentAdmin } = useAdminAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () =>
    adminAxios.get("/admin/admins").then(({ data }) => setAdmins(data)).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError("");
    setShowForm(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({ email: a.email, password: "", name: a.name || "" });
    setError("");
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      if (editing) {
        const payload = { email: form.email, name: form.name };
        if (form.password) payload.password = form.password;
        await adminAxios.put(`/admin/admins/${editing.id}`, payload);
      } else {
        if (!form.password) { setError("Password required"); setSaving(false); return; }
        await adminAxios.post("/admin/admins", form);
      }
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Error saving admin");
    } finally {
      setSaving(false);
    }
  };

  const del = async (a) => {
    if (!window.confirm(`Remove admin "${a.email}"? This action cannot be undone.`)) return;
    try {
      await adminAxios.delete(`/admin/admins/${a.id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Cannot delete admin");
    }
  };

  const isSelf = (a) => a.id === currentAdmin?.id;

  return (
    <div className="p-8 min-h-screen bg-zinc-50">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">Admin Users</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Manage CMS access — admin accounts and permissions</p>
        </div>
        <button
          onClick={openCreate}
          data-testid="add-admin-btn"
          className="flex items-center gap-2 bg-[#C8102E] hover:bg-[#A50D25] text-white text-xs tracking-wider uppercase px-5 py-2.5 transition-colors"
        >
          <Plus size={14} strokeWidth={2} /> Add Admin
        </button>
      </div>

      <div className="bg-white border border-zinc-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100">
              {["Name", "Email", "Created", ""].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-4 py-6 text-zinc-400 text-sm">Loading...</td></tr>
            ) : admins.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-8 text-zinc-400 text-center text-sm">No admins found.</td></tr>
            ) : admins.map(a => (
              <tr
                key={a.id}
                className="border-b border-zinc-50 hover:bg-zinc-50 transition-colors"
                data-testid={`admin-row-${a.id}`}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {a.name
                      ? <span className="font-medium text-zinc-900">{a.name}</span>
                      : <span className="text-zinc-400 text-xs italic">—</span>
                    }
                    {isSelf(a) && (
                      <span className="flex items-center gap-1 text-[10px] font-medium text-[#C8102E] bg-red-50 px-2 py-0.5 tracking-wider uppercase">
                        <ShieldCheck size={10} /> You
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-zinc-600">{a.email}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs">
                  {a.created_at ? new Date(a.created_at).toLocaleDateString("it-IT") : "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => openEdit(a)}
                      data-testid={`edit-admin-${a.id}`}
                      title="Edit / Change password"
                      className="text-zinc-400 hover:text-zinc-700 transition-colors"
                    >
                      <Edit2 size={13} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => openEdit(a)}
                      data-testid={`pwd-admin-${a.id}`}
                      title="Change password"
                      className="text-zinc-400 hover:text-zinc-700 transition-colors"
                    >
                      <KeyRound size={13} strokeWidth={1.5} />
                    </button>
                    {!isSelf(a) && (
                      <button
                        onClick={() => del(a)}
                        data-testid={`delete-admin-${a.id}`}
                        title="Remove admin"
                        className="text-zinc-400 hover:text-[#C8102E] transition-colors"
                      >
                        <Trash2 size={13} strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-white max-w-sm p-7">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-zinc-900">
              {editing ? "Edit Admin" : "New Admin"}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-400 mt-1">
              {editing ? "Update email, name or set a new password." : "Create a new CMS admin account."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={save} className="space-y-4 mt-4">
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Mario Rossi"
                className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400"
                data-testid="admin-form-name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                required
                placeholder="admin@scarlet.properties"
                className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400"
                data-testid="admin-form-email"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">
                Password {editing && <span className="text-zinc-400 font-normal">(lascia vuoto per non cambiare)</span>}
              </label>
              <input
                type="password"
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder={editing ? "••••••••" : "Minimo 8 caratteri"}
                required={!editing}
                minLength={editing ? 0 : 8}
                className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400"
                data-testid="admin-form-password"
              />
            </div>

            {error && (
              <p className="text-xs text-[#C8102E]" data-testid="admin-form-error">{error}</p>
            )}

            <div className="flex gap-3 pt-1">
              <button
                type="submit"
                disabled={saving}
                data-testid="save-admin-btn"
                className="bg-[#C8102E] hover:bg-[#A50D25] text-white text-xs tracking-wider uppercase px-6 py-2.5 transition-colors disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="border border-zinc-200 text-zinc-600 text-xs uppercase px-6 py-2.5 hover:bg-zinc-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
